import { Route, Switch, Link, useLocation } from "wouter";
import { useEffect, useState } from "react";
import { hasAdminKey } from "./api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, BarChart3, TrendingUp, Folder, Tag, Megaphone, CreditCard, Palette, FileText, Database, BadgeCheck, ShieldCheck, Gem, MessageCircleQuestion, Handshake, Flag, MessagesSquare, PartyPopper, Newspaper } from "lucide-react";
import { getCurrentUser } from "@/lib/firebase";
import Dashboard from "./Dashboard";
import ArticlesManagement from "./ArticlesManagement";
import RankingsManagement from "./RankingsManagement";
import CategoriesManagement from "./CategoriesManagement";
import TagsManagement from "./TagsManagement";
import AdsManagement from "./AdsManagement";
import Billing from "./Billing";
import ThemeManagement from "./ThemeManagement";
import BusinessImportsManagement from "./BusinessImportsManagement";
import BusinessClaimsManagement from "./BusinessClaimsManagement";
import BusinessTrustOperations from "./BusinessTrustOperations";
import BusinessSubscriptionManagement from "./BusinessSubscriptionManagement";
import HelpRequestsManagement from "./HelpRequestsManagement";
import BusinessPartnerProfiles from "./BusinessPartnerProfiles";
import ReportsManagement from "./ReportsManagement";
import CommunityTalkModeration from "./CommunityTalkModeration";
import CityClassifiedsModeration from "./CityClassifiedsModeration";
import NewsCandidatesQueue from "./NewsCandidatesQueue";

function AdminNav() {
  const [location] = useLocation();

  const navItems = [
    { path: "/admin", label: "Dashboard", icon: BarChart3 },
    { path: "/admin/articles", label: "Articles", icon: FileText },
    { path: "/admin/news-candidates", label: "뉴스 후보 큐", icon: Newspaper },
    { path: "/admin/rankings", label: "Rankings", icon: TrendingUp },
    { path: "/admin/categories", label: "Categories", icon: Folder },
    { path: "/admin/tags", label: "Tags", icon: Tag },
    { path: "/admin/ads", label: "동네업체 광고", icon: Megaphone },
    { path: "/admin/billing", label: "Billing", icon: CreditCard },
    { path: "/admin/theme", label: "Theme", icon: Palette },
    { path: "/admin/business-imports", label: "Business Imports", icon: Database },
    { path: "/admin/business-claims", label: "Business Claims", icon: BadgeCheck },
    { path: "/admin/business-trust", label: "Business Trust", icon: ShieldCheck },
    { path: "/admin/business-premium", label: "Business Premium", icon: Gem },
    { path: "/admin/help-requests", label: "Help Requests", icon: MessageCircleQuestion },
    { path: "/admin/business-partners", label: "Founding Partners", icon: Handshake },
    { path: "/admin/reports", label: "Reports", icon: Flag },
    { path: "/admin/community-talk", label: "동네 톡톡", icon: MessagesSquare },
    { path: "/admin/city-classifieds", label: "생활광고·모임", icon: PartyPopper },
  ];

  return (
    <nav className="flex gap-2 p-4 bg-card border-b overflow-x-auto">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location === item.path;

        return (
          <Link key={item.path} href={item.path}>
            <Button
              variant={isActive ? "default" : "ghost"}
              size="sm"
              className="whitespace-nowrap"
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {item.label}
            </Button>
          </Link>
        );
      })}
    </nav>
  );
}

function AccessDenied() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Shield className="w-5 h-5" />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You need administrator credentials to access this area.
          </p>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>To enable admin access:</p>
            <ol className="list-decimal list-inside ml-2 space-y-1">
              <li>Set <code className="bg-muted px-1 rounded">VITE_ADMIN_API_KEY</code> environment variable</li>
              <li>Restart the development server</li>
              <li>Refresh this page</li>
            </ol>
          </div>
          <Button variant="outline" onClick={() => window.location.href = "/"}>
            Return to Home
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminApp() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log("%c[Admin App] 🔍 Checking authentication...", "color: blue; font-weight: bold");
        const user = await getCurrentUser();
        console.log("%c[Admin App] 👤 Current user:", "color: blue; font-weight: bold", user ? `✅ ${user.email}` : "❌ not logged in");

        if (user) {
          console.log("%c[Admin App] ✅ User is authenticated", "color: green; font-weight: bold");
          // Try to get token to verify it works
          try {
            const token = await user.getIdToken(true);
            console.log("%c[Admin App] ✅ Token obtained (length:", "color: green; font-weight: bold", token?.length || 0, ")");
          } catch (tokenError) {
            console.error("%c[Admin App] ❌ Failed to get token:", "color: red; font-weight: bold", tokenError);
          }
        } else {
          console.warn("%c[Admin App] ⚠️ No user found - login required", "color: orange; font-weight: bold");
        }

        setIsAuthenticated(!!user);
      } catch (error) {
        console.error("%c[Admin App] ❌ Error checking auth:", "color: red; font-weight: bold", error);
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Check if admin key is configured
  if (!hasAdminKey()) {
    return <AccessDenied />;
  }

  if (isChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-8 h-8 animate-pulse mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <Shield className="w-5 h-5" />
              Login Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You must be logged in to access the admin dashboard.
            </p>
            <Button onClick={() => window.location.href = "/login"}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-card">
        <div className="container mx-auto py-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">OZK News Management</p>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="container mx-auto p-6">
        <Switch>
          <Route path="/admin" component={Dashboard} />
          <Route path="/admin/articles" component={ArticlesManagement} />
          <Route path="/admin/news-candidates" component={NewsCandidatesQueue} />
          <Route path="/admin/rankings" component={RankingsManagement} />
          <Route path="/admin/categories" component={CategoriesManagement} />
          <Route path="/admin/tags" component={TagsManagement} />
          <Route path="/admin/ads" component={AdsManagement} />
          <Route path="/admin/billing" component={Billing} />
          <Route path="/admin/theme" component={ThemeManagement} />
          <Route path="/admin/business-imports" component={BusinessImportsManagement} />
          <Route path="/admin/business-claims" component={BusinessClaimsManagement} />
          <Route path="/admin/business-trust" component={BusinessTrustOperations} />
          <Route path="/admin/business-premium" component={BusinessSubscriptionManagement} />
          <Route path="/admin/help-requests" component={HelpRequestsManagement} />
          <Route path="/admin/business-partners" component={BusinessPartnerProfiles} />
          <Route path="/admin/reports" component={ReportsManagement} />
          <Route path="/admin/community-talk" component={CommunityTalkModeration} />
          <Route path="/admin/city-classifieds" component={CityClassifiedsModeration} />
        </Switch>
      </div>
    </div>
  );
}
